/**
 * Sync bridge: fires D1 push after local IndexedDB writes when the signed-in workspace is active.
 * Call these after any db.* write to keep D1 in sync.
 */

import { getStoredAuth } from "./auth";
import { useAppStore } from "./store";
import { pushServer, pushQuery, pushVaultEntry, deleteRemoteServer, deleteRemoteQuery, deleteRemoteVaultEntry } from "./userDataClient";
import type { StoredServer, StoredQuery } from "./persistence";
import { defaultQueryOperationConfig, defaultServerConnectionConfig, normalizeRequestType, normalizeServerKind } from "@spuerhund/shared/targets";

function shouldSync(): boolean {
  const auth = getStoredAuth();
  const mode = useAppStore.getState().mode;
  return !!auth?.accessToken && mode === "signed-in";
}

// ── Server sync ──────────────────────────────────────────────────────

export function syncServerToD1(server: StoredServer): void {
  if (!shouldSync()) return;
  pushServer({
    id: server.id,
    user_id: "", // filled by server from JWT
    name: server.name,
    server_kind: normalizeServerKind(server.serverKind),
    connection_config: JSON.stringify(defaultServerConnectionConfig(server)),
    base_url: server.baseUrl,
    graphql_path: server.graphqlPath ?? null,
    rest_path: server.restPath ?? null,
    auth_type: server.authType,
    auth_config: server.authConfig,
    is_production: server.isProduction ? 1 : 0,
    timeout_ms: server.timeoutMs ?? null,
    sort_order: server.sortOrder,
    created_at: server.createdAt,
    updated_at: server.updatedAt,
    version: 1,
  }).catch((err) => console.warn("[sync] server push failed", err));
}

export function syncDeleteServerFromD1(serverId: string): void {
  if (!shouldSync()) return;
  deleteRemoteServer(serverId).catch((err) => console.warn("[sync] server delete failed", err));
}

// ── Query sync ───────────────────────────────────────────────────────

export function syncQueryToD1(query: StoredQuery): void {
  if (!shouldSync()) return;
  const requestType = normalizeRequestType(query.requestType);
  const operationConfig = defaultQueryOperationConfig(query) ?? query.operationConfig;
  pushQuery({
    id: query.id,
    user_id: "",
    server_id: query.serverId,
    name: query.name,
    query_text: query.queryText,
    variables: query.variables,
    headers: query.headers,
    remarks: query.remarks ?? "",
    risk_level: query.riskLevel ?? "normal",
    sort_order: query.sortOrder,
    category: query.category ?? null,
    system_preset: query.systemPreset ?? null,
    request_type: requestType,
    operation_config: requestType === "mongo" || requestType === "sql"
      ? JSON.stringify(operationConfig)
      : null,
    rest_method: query.restMethod ?? null,
    rest_path: query.restPath ?? null,
    rest_params: query.restParams ? JSON.stringify(query.restParams) : null,
    rest_content_type: query.restContentType ?? null,
    pinned_at: query.pinnedAt ?? null,
    last_used_at: query.lastUsedAt ?? null,
    created_at: query.createdAt,
    updated_at: query.updatedAt,
    version: 1,
  }).catch((err) => console.warn("[sync] query push failed", err));
}

export function syncDeleteQueryFromD1(queryId: string): void {
  if (!shouldSync()) return;
  deleteRemoteQuery(queryId).catch((err) => console.warn("[sync] query delete failed", err));
}

// ── Vault sync ───────────────────────────────────────────────────────

export function syncVaultEntryToD1(entry: {
  id: string;
  serverId?: string;
  kind: string;
  key: string;
  ciphertext: string;
  iv: string;
  createdAt?: string;
  updatedAt?: string;
}): void {
  if (!shouldSync()) return;
  const now = new Date().toISOString();
  pushVaultEntry({
    id: entry.id,
    user_id: "",
    server_id: entry.serverId ?? null,
    kind: entry.kind,
    key: entry.key,
    encrypted_value: `${entry.iv}:${entry.ciphertext}`, // pack iv+ciphertext together
    created_at: entry.createdAt ?? now,
    updated_at: entry.updatedAt ?? now,
  }).catch((err) => console.warn("[sync] vault push failed", err));
}

export function syncDeleteVaultEntryFromD1(entryId: string): void {
  if (!shouldSync()) return;
  deleteRemoteVaultEntry(entryId).catch((err) => console.warn("[sync] vault delete failed", err));
}
